import { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { Monitor } from '../../types/monitoring';
import { MonitorSetupModal } from './MonitorSetupModal';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { subscribeToMonitorEvents } from '../../utils/monitorEvents';

interface MonitorButtonProps {
  tableId: string;
  tableName: string;
  websetId?: string;
  disabled?: boolean;
  onStartChatSetup?: () => void; // When provided, triggers chat-based setup instead of modal
}

export interface MonitorButtonRef {
  refresh: () => Promise<void>;
}

export const MonitorButton = forwardRef<MonitorButtonRef, MonitorButtonProps>(({
  tableId,
  tableName,
  websetId,
  disabled = false,
  onStartChatSetup,
}, ref) => {
  const { openSettings, onMonitorChange } = useSettingsModal();
  const [monitor, setMonitor] = useState<Monitor | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);

  // Track whether component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // Reusable function to fetch monitor for this table
  const fetchMonitor = useCallback(
    async (showLoading = false) => {
      if (!tableId) return;

      try {
        if (showLoading) setIsLoading(true);
        // Filter at DB level for efficiency
        const response = await apiClient.listMonitors(tableId);
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setMonitor(response.monitors[0] || null);
        }
      } catch (err) {
        console.error('Failed to load monitor:', err);
      } finally {
        if (isMountedRef.current && showLoading) {
          setIsLoading(false);
        }
      }
    },
    [tableId]
  );

  // Load monitor on mount, cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    fetchMonitor(true);

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchMonitor]);

  // Subscribe to monitor changes (e.g., when monitor is deleted from settings)
  useEffect(() => {
    const unsubscribe = onMonitorChange(() => {
      fetchMonitor(false);
    });
    return unsubscribe;
  }, [onMonitorChange, fetchMonitor]);

  // Subscribe to monitor events from chat actions
  useEffect(() => {
    const unsubscribe = subscribeToMonitorEvents((eventType, detail) => {
      // Only refresh if the event is for this table or no specific table
      if (!detail?.tableId || detail.tableId === tableId) {
        if (eventType === 'monitor:deleted') {
          // Immediately clear monitor state for faster UI response
          setMonitor(null);
        }
        // Refresh monitor data from server
        fetchMonitor();
      }
    });

    return unsubscribe;
  }, [tableId, fetchMonitor]);

  // Expose refresh method to parent components
  useImperativeHandle(ref, () => ({
    refresh: () => fetchMonitor(false)
  }), [fetchMonitor]);

  const handleMonitorCreated = async () => {
    setShowWizard(false);
    await fetchMonitor();
  };

  const handleClick = () => {
    if (monitor) {
      // Open settings modal to monitors section with this monitor selected
      openSettings('monitors', monitor.id);
    } else if (onStartChatSetup) {
      // Use chat-based setup if callback is provided
      onStartChatSetup();
    } else {
      setShowWizard(true);
    }
  };

  // Don't show button if no webset (can't create monitor without webset)
  if (!websetId) {
    return null;
  }

  if (isLoading) {
    return (
      <button
        disabled
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium text-gray-400 rounded-md cursor-not-allowed"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Monitor</span>
      </button>
    );
  }

  const isEnabled = monitor?.status === 'enabled';

  return (
    <>
      {monitor ? (
        // Has monitor - show compact status button with pulsing dot if active
        <button
          onClick={handleClick}
          disabled={disabled}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            isEnabled ? 'text-gray-700' : 'text-gray-500'
          }`}
          title={isEnabled ? 'Monitoring active - click to configure' : 'Monitor paused - click to configure'}
        >
          <span className="relative flex items-center justify-center">
            <span
              className={`w-2 h-2 rounded-full ${isEnabled ? 'bg-green-500' : 'bg-gray-400'}`}
            />
            {isEnabled && (
              <span className="absolute w-2 h-2 rounded-full bg-green-400 animate-ping opacity-75" />
            )}
          </span>
          <span>{isEnabled ? 'Monitoring' : 'Paused'}</span>
        </button>
      ) : (
        // No monitor - show create button
        <button
          onClick={handleClick}
          disabled={disabled}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Set up automatic monitoring"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Monitor</span>
        </button>
      )}

      {/* Monitor Setup Modal */}
      {showWizard && (
        <MonitorSetupModal
          tableId={tableId}
          tableName={tableName}
          onClose={() => setShowWizard(false)}
          onComplete={handleMonitorCreated}
        />
      )}
    </>
  );
});

MonitorButton.displayName = 'MonitorButton';

export default MonitorButton;
